/**
 * Trip register — every trip in a window, billed or not.
 *
 * The operations report, and the one that answers the question an invoice
 * cannot: what has been run and not yet charged for. The invoice column
 * names the document that carried each trip, so an unbilled trip reads as
 * a gap in a column rather than something you have to work out.
 *
 * Filters by vehicle as well as by customer, because the owner asks two
 * questions and only one of them is about a customer. "What did 1272 DNA
 * do last month" is how an idle unit gets noticed.
 *
 * The figures along the top are counts and totals over exactly the rows
 * below them, computed by the server over the same query -- including
 * after per-user scoping, so a salesman's totals match his own list
 * rather than the whole fleet's.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Chip, MenuItem, Skeleton, Stack, TextField, Typography,
  useMediaQuery,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Figure, FigureGrid } from '../reports/SummaryFigures'
import { useBi } from '../components/Bi'

import { fetchTrips, fetchVehicles } from '../api/endpoints'
import type { Trip } from '../api/types'
import { c, fmtMoney } from '../theme'
import ReportShell, { rangeLabel } from '../reports/ReportShell'
import ReportFooter from '../reports/ReportFooter'
import ReportToolbar from '../reports/ReportToolbar'
import { useDateRange } from '../reports/useDateRange'
import { downloadCsv } from '../reports/csv'
import { formatKg, useTripRef } from './tripRef'
import { gregorian } from '../reports/hijri'
import LedgerPicker from '../vouchers/LedgerPicker'

/** One figure with its label. Reads as a fact, not a dashboard tile. */
export default function TripRegister() {
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const { range, setRange, presets } = useDateRange()
  const [customer, setCustomer] = useState('')
  const [vehicle, setVehicle] = useState('')
  const [billing, setBilling] = useState<'all' | 'unbilled'>('all')
  const narrow = useMediaQuery('(max-width:760px)')
  const weight = useTripRef() === 'WEIGHT'

  const vehicles = useQuery({ queryKey: ['vehicles'],
    queryFn: () => fetchVehicles() })

  const q = useQuery({
    queryKey: ['trips', 'register', range.from, range.to, customer, vehicle,
               billing],
    queryFn: () => fetchTrips({
      from_date: range.from, to_date: range.to,
      customer_code: customer || undefined,
      vehicle_plate: vehicle || undefined,
      unbilled_only: billing === 'unbilled' }),
  })

  const trips: Trip[] = q.data?.trips ?? []
  const totals = q.data?.totals

  return (
    <ReportShell titleKey="nav.tripRegister"
      period={rangeLabel(range.from, range.to, i18n.language, t)}
      headRight={q.data
        ? <Chip size="small" variant="outlined" label={q.data.count} />
        : undefined}
      toolbar={
        <ReportToolbar range={range} setRange={setRange} presets={presets}
          pdf={{ path: 'trip-register.pdf',
                 params: { from_date: range.from, to_date: range.to,
                           customer_code: customer || undefined,
                           vehicle_plate: vehicle || undefined,
                           unbilled_only: billing === 'unbilled'
                             ? 'true' : undefined },
                 filename: `trip-register-${range.from}-${range.to}.pdf` }}
          onExport={() => downloadCsv(
            `trip-register-${range.from}-${range.to}`, [
              ['Date', 'Trip', 'Customer', 'Vehicle', 'From', 'To',
               'Container', 'DO', weight ? 'Weight (kg)' : 'Way bill',
               'Rate', 'Hire cost', 'Invoice'],
              ...trips.map((r) => [
                r.trip_date, r.trip_no, r.customer_name ?? '',
                r.vehicle_plate ?? '', r.from_place, r.to_place,
                r.container_no ?? '', r.do_number ?? '',
                weight ? (r.weight_kg ?? '') : (r.way_bill ?? ''),
                r.rate, r.subcontract_cost ?? '', r.invoice_no ?? '',
              ]),
            ])}>
          <Box sx={{ position: 'relative', minWidth: 220 }}>
            <LedgerPicker value={customer} onChange={setCustomer}
              groups={['DEBTORS']}
              placeholder={t('trip.customerPlaceholder')} />
          </Box>
          <TextField select size="small" label={t('trip.vehicle')}
            value={vehicle} onChange={(e) => setVehicle(e.target.value)}
            sx={{ width: 150 }}>
            <MenuItem value="">{t('report.allVehicles')}</MenuItem>
            {(vehicles.data ?? []).map((v) => (
              <MenuItem key={v.id} value={v.plate_no}>{v.plate_no}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label={t('report.billing')}
            value={billing}
            onChange={(e) => setBilling(e.target.value as typeof billing)}
            sx={{ width: 150 }}>
            <MenuItem value="all">{t('report.allTrips')}</MenuItem>
            <MenuItem value="unbilled">{t('report.unbilledOnly')}</MenuItem>
          </TextField>
        </ReportToolbar>
      }>

      {totals && (
        <FigureGrid>
          <Figure label={bi('report.tripRevenue')} value={fmtMoney(totals.revenue)} strong />
          <Figure label={bi('report.hireCost')}
            value={fmtMoney(totals.subcontract_cost)} />
          {weight && (
            <Figure label={bi('trip.weightKg')}
              value={formatKg(totals.weight_kg ?? '0')} />
          )}
          <Figure label={bi('report.billedTrips')} value={String(totals.billed)} />
          {/* Gold, not red: unbilled work is not an error, it is the
              thing to go and do something about. */}
          <Figure label={bi('report.unbilledTrips')}
            value={String(totals.unbilled)}
            tone={totals.unbilled > 0 ? 'warn' : undefined} />
        </FigureGrid>
      )}

      {q.isLoading ? (
        <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
      ) : trips.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
          {t('report.nothingInRange')}
        </Typography>
      ) : narrow ? (
        <Box className="ruled">
          {trips.map((r) => (
            <Box key={r.id} sx={{ px: 2, py: 1.25 }}>
              <Stack direction="row" justifyContent="space-between"
                alignItems="baseline" spacing={1}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, minWidth: 0 }}
                  noWrap>
                  {r.customer_name ?? '—'}
                </Typography>
                <span className="num" style={{ fontWeight: 600 }}>
                  {fmtMoney(r.rate)}
                </span>
              </Stack>
              <Stack direction="row" justifyContent="space-between"
                sx={{ mt: 0.25, color: c.inkMuted, fontSize: 11.5 }}>
                <span className="num">
                  {gregorian(r.trip_date, i18n.language)} · {r.vehicle_plate}
                  {weight && r.weight_kg ? ` · ${formatKg(r.weight_kg)} kg` : ''}
                </span>
                <span className="num" style={{
                  color: r.invoice_no ? c.inkMuted : c.warningInk }}>
                  {r.invoice_no ?? t('report.notBilled')}
                </span>
              </Stack>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 1 }}>
          <table className="rpt">
            <thead>
              <tr>
                <th style={{ width: 100 }}>{t('entry.date')}</th>
                <th style={{ width: 138 }}>{t('trip.tripNo')}</th>
                <th>{t('trip.customer')}</th>
                <th style={{ width: 104 }}>{t('trip.vehicle')}</th>
                <th style={{ width: 170 }}>{t('trip.route')}</th>
                <th style={{ width: 118 }}>{t('trip.container')}</th>
                {weight && (
                  <th className="num" style={{ width: 110 }}>{t('trip.weightKg')}</th>
                )}
                <th className="num" style={{ width: 110 }}>{t('trip.rate')}</th>
                <th style={{ width: 140 }}>{t('report.invoice')}</th>
              </tr>
            </thead>
            <tbody>
              {trips.map((r) => (
                <tr key={r.id} style={{
                  opacity: r.status === 'CANCELLED' ? 0.55 : 1 }}>
                  <td className="num" style={{ textAlign: 'start', whiteSpace: 'nowrap' }}>
                    {gregorian(r.trip_date, i18n.language)}</td>
                  <td className="num" style={{ textAlign: 'start',
                    color: 'var(--ink-muted)', fontSize: '0.78rem' }}>
                    {r.trip_no}</td>
                  <td>{r.customer_name ?? '—'}</td>
                  <td className="num" style={{ textAlign: 'start' }}>
                    {r.vehicle_plate}
                    {r.vehicle_kind === 'SUBCONTRACTED' && (
                      <Chip size="small" label={t('trip.hired')}
                        sx={{ ml: 0.75, height: 15, fontSize: 9,
                              bgcolor: c.warningSoft, color: c.warningInk }} />
                    )}
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>
                    {r.from_place && r.to_place
                      ? `${r.from_place} → ${r.to_place}` : '—'}</td>
                  <td className="num" style={{ textAlign: 'start',
                    fontSize: '0.78rem' }}>
                    {r.container_no ?? r.do_number ?? ''}</td>
                  {weight && <td className="num">{formatKg(r.weight_kg)}</td>}
                  <td className="num">{fmtMoney(r.rate)}</td>
                  {/* Never blank: an empty cell reads as a column that is
                      never filled in, rather than as work to invoice. */}
                  <td className="num" style={{ textAlign: 'start',
                    fontSize: '0.78rem',
                    color: r.invoice_no ? 'var(--ink-muted)' : c.warningInk }}>
                    {r.invoice_no ?? t('report.notBilled')}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6}>{t('report.total')}</td>
                {weight && (
                  <td className="num">{formatKg(totals?.weight_kg ?? '0')}</td>
                )}
                <td className="num">{fmtMoney(totals?.revenue ?? '0')}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </Box>
      )}

      <Typography variant="caption"
        sx={{ display: 'block', px: 2, pb: 2, color: c.inkMuted }}>
        {t('report.tripRegisterNote')}
      </Typography>
      <ReportFooter />
    </ReportShell>
  )
}
